import React, { useState, useEffect } from 'react';
import { Wrench, Mail, Coffee, Code, Zap, Heart } from 'lucide-react';

export default function App() {
  const [dots, setDots] = useState('');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const floatingIcons = [
    { Icon: Code, delay: 0, x: 10, y: 20 },
    { Icon: Coffee, delay: 1, x: 80, y: 15 },
    { Icon: Zap, delay: 2, x: 15, y: 70 },
    { Icon: Heart, delay: 0.5, x: 85, y: 75 },
  ];

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #581c87 0%, #1e3a8a 50%, #3730a3 100%)',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    backgroundParticles: {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none'
    },
    particle: {
      position: 'absolute',
      width: '4px',
      height: '4px',
      backgroundColor: 'white',
      borderRadius: '50%',
      opacity: 0.3
    },
    gradientOrb: {
      position: 'absolute',
      width: '384px',
      height: '384px',
      borderRadius: '50%',
      opacity: 0.2,
      filter: 'blur(60px)',
      background: 'radial-gradient(circle, rgba(168,85,247,0.4) 0%, rgba(59,130,246,0.4) 50%, rgba(147,51,234,0.4) 100%)',
      transition: 'all 1s ease',
      pointerEvents: 'none',
      left: mousePos.x - 192,
      top: mousePos.y - 192,
      transform: `scale(${isHovering ? 1.2 : 1})`
    },
    floatingIcon: {
      position: 'absolute',
      opacity: 0.1,
      color: 'white',
      pointerEvents: 'none'
    },
    mainContent: {
      position: 'relative',
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '32px'
    },
    contentWrapper: {
      textAlign: 'center',
      maxWidth: '672px',
      margin: '0 auto'
    },
    glassCard: {
      backdropFilter: 'blur(16px)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '24px',
      padding: '48px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transform: isHovering ? 'scale(1.05)' : 'scale(1)',
      transition: 'all 0.5s ease'
    },
    iconContainer: {
      position: 'relative',
      marginBottom: '32px'
    },
    wrenchIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '96px',
      height: '96px',
      background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
      borderRadius: '50%',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
      transition: 'transform 0.5s ease',
      transform: isHovering ? 'rotate(12deg)' : 'rotate(0deg)'
    },
    sparkle: {
      position: 'absolute',
      borderRadius: '50%',
      backgroundColor: '#fde047'
    },
    sparkle1: {
      top: '-8px',
      right: '-8px',
      width: '16px',
      height: '16px'
    },
    sparkle2: {
      bottom: '-8px',
      left: '-8px',
      width: '12px',
      height: '12px',
      backgroundColor: '#93c5fd'
    },
    mainHeading: {
      fontSize: '4rem',
      fontWeight: '900',
      color: 'white',
      marginBottom: '16px',
      background: 'linear-gradient(45deg, #fbbf24, #ec4899, #8b5cf6)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      lineHeight: 1.1
    },
    subHeading: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: 'white',
      marginBottom: '32px'
    },
    description: {
      fontSize: '1.25rem',
      color: '#dbeafe',
      marginBottom: '48px',
      lineHeight: 1.6,
      fontWeight: '500'
    },
    progressSection: {
      marginBottom: '48px'
    },
    progressHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.875rem',
      color: '#bfdbfe',
      marginBottom: '8px'
    },
    progressBar: {
      width: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '9999px',
      height: '12px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #34d399 0%, #3b82f6 100%)',
      borderRadius: '9999px',
      width: '78%',
      position: 'relative',
      transition: 'all 1s ease'
    },
    progressGlow: {
      position: 'absolute',
      inset: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '9999px'
    },
    contactSection: {
      marginBottom: '48px'
    },
    contactText: {
      color: '#bfdbfe',
      fontSize: '1.125rem',
      fontWeight: '600',
      marginBottom: '24px'
    },
    contactButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      background: 'linear-gradient(90deg, #ec4899 0%, #8b5cf6 100%)',
      color: 'white',
      fontWeight: 'bold',
      padding: '16px 32px',
      borderRadius: '9999px',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      transform: isHovering ? 'scale(1.1)' : 'scale(1)',
      boxShadow: isHovering ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 10px 25px rgba(0, 0, 0, 0.3)'
    },
    funMessage: {
      marginTop: '48px',
      padding: '24px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    funMessageText: {
      color: '#dbeafe',
      fontStyle: 'italic',
      margin: 0
    },
    footer: {
      color: '#93c5fd',
      marginTop: '32px',
      fontSize: '1.125rem',
      fontWeight: '500'
    }
  };

  return (
    <div style={styles.container}>
      {/* Animated Background Particles */}
      <div style={styles.backgroundParticles}>
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.particle,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animation: 'twinkle 3s infinite ease-in-out'
            }}
          />
        ))}
      </div>

      {/* Interactive Gradient Orb */}
      <div style={styles.gradientOrb} />

      {/* Floating Icons */}
      {floatingIcons.map(({ Icon, delay, x, y }, index) => (
        <div
          key={index}
          style={{
            ...styles.floatingIcon,
            left: `${x}%`,
            top: `${y}%`,
            animation: `float 6s ease-in-out infinite`,
            animationDelay: `${delay}s`
          }}
        >
          <Icon size={32} />
        </div>
      ))}

      {/* Main Content */}
      <div style={styles.mainContent}>
        <div style={styles.contentWrapper}>
          {/* Glass Card */}
          <div
            style={styles.glassCard}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Animated Wrench Icon */}
            <div style={styles.iconContainer}>
              <div style={styles.wrenchIcon}>
                <Wrench size={40} color="white" style={{ animation: 'pulse 2s infinite' }} />
              </div>
              {/* Sparkle Effects */}
              <div style={{...styles.sparkle, ...styles.sparkle1, animation: 'ping 1s infinite'}} />
              <div style={{...styles.sparkle, ...styles.sparkle2, animation: 'ping 1s infinite', animationDelay: '0.5s'}} />
            </div>

            {/* Main Heading */}
            <h1 style={styles.mainHeading}>
              We're Building
            </h1>
            <h2 style={styles.subHeading}>
              Something Epic{dots}
            </h2>

            {/* Description */}
            <p style={styles.description}>
              Our digital craftsmen are hard at work, brewing up something extraordinary. 
              We're adding new features, squashing bugs, and making everything more awesome than before!
            </p>

            {/* Progress Bar */}
            <div style={styles.progressSection}>
              <div style={styles.progressHeader}>
                <span>Progress</span>
                <span>Almost there...</span>
              </div>
              <div style={styles.progressBar}>
                <div style={styles.progressFill}>
                  <div style={{...styles.progressGlow, animation: 'pulse 2s infinite'}} />
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div style={styles.contactSection}>
              <p style={styles.contactText}>
                Need to reach us? We're just a click away!
              </p>
              
              <a
                href="mailto:support@heybruh.in"
                style={styles.contactButton}
              >
                <Mail size={24} style={{ animation: isHovering ? 'bounce 1s infinite' : 'none' }} />
                support@heybruh.in
              </a>
            </div>

            {/* Fun Message */}
            <div style={styles.funMessage}>
              <p style={styles.funMessageText}>
                "Great things take time, but epic things take a little longer! 🚀"
              </p>
            </div>
          </div>

          {/* Footer */}
          <p style={styles.footer}>
            Thanks for your patience - it'll be worth the wait! ✨
          </p>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(5deg); }
          66% { transform: translateY(10px) rotate(-3deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
            transform: translateY(0);
          }
          40%, 43% {
            animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
            transform: translateY(-8px);
          }
          70% {
            animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
            transform: translateY(-4px);
          }
          90% {
            transform: translateY(-2px);
          }
        }
      `}</style>
    </div>
  );
}